import { HeaderText } from '../header/HeaderText';
import ProveText from '../prove/ProveText';
import { resolveNavItem } from './resolveNavItem';
import useCustomerConfig from 'src/hooks/useCustomerConfig';
import useAuthentication from 'src/hooks/useAuthentication';
import useTranslate from 'src/hooks/useTranslate';
import useRouteCheck from 'src/hooks/useRouteCheck';
import { getEnvVariable } from 'src/utils/envUtils';
import { useCustomEmployeeText } from 'src/utils/CustomExperienceText';
import { STATEMENT_OF_GOOD_HEALTH_GROUP_ID } from 'src/constants/navGroupIds';
/**
 * Replaces FooterNavigation.tsx's rendering with plain FooterColumn[] data.
 * navMenu entries with no navGroups (just a navLink, e.g. BDM -> /bdm, or
 * FORMS_AND_DOCS -> /employer/resource-center) render as a plain-text
 * heading with a single link underneath (matching real production — the
 * heading itself is not the link).
 */
export function buildFooterColumns(params) {
    const { navigationMenu = [], isAuthenticated, user, isCommPrefEnabled, updatedEmployerEmailNotificationsEnabled, lfgHomeUrl, translate, navigationCheck, onNavigate, } = params;
    const resolveContext = {
        translate,
        isIdentityVerified: user?.isIdentityVerified,
        navigationCheck,
        onNavigate,
    };
    const columns = navigationMenu.map((navMenu) => {
        const heading = translate(navMenu.navLabel);
        if (navMenu.navGroups && navMenu.navGroups.length > 0) {
            return {
                heading,
                categories: navMenu.navGroups.map((group) => ({
                    subHeading: group.groupLabel && group.id !== STATEMENT_OF_GOOD_HEALTH_GROUP_ID ? translate(group.groupLabel) : undefined,
                    links: group.navLinks.map((link) => resolveNavItem(link, resolveContext)),
                })),
            };
        }
        // A solo top-level navLink's own `label` is often omitted by the nav-menu API (the
        // outer navLabel is treated as authoritative for the whole entry) - fall back to it,
        // mirroring NavMenuBar/buildNavItems.ts's identical fallback for the same API shape.
        return {
            heading,
            categories: navMenu.navLink
                ? [{ links: [resolveNavItem({ ...navMenu.navLink, label: navMenu.navLink.label ?? navMenu.navLabel }, resolveContext)] }]
                : [],
        };
    });
    if (isAuthenticated && user) {
        columns.push(buildProfileAndSupportColumn({ user, isCommPrefEnabled, updatedEmployerEmailNotificationsEnabled, lfgHomeUrl, translate }));
    }
    return columns;
}
function buildProfileAndSupportColumn(params) {
    const { user, isCommPrefEnabled, updatedEmployerEmailNotificationsEnabled, lfgHomeUrl, translate: t } = params;
    const links = [];
    if (user.isLfgUser && !user.isIdentityVerified) {
        links.push({ label: t(ProveText.verifyHeadingAlt), url: '/profile/accountAccess' });
    }
    const isVerifiedMlpEmployee = user.auth0Roles?.includes('MLPEmployee') ? user.isIdentityVerified : true;
    if (user.isLfgUser && isVerifiedMlpEmployee) {
        if (user.auth0Roles?.includes('PA') || user.auth0Roles?.includes('MLPEmployee')) {
            links.push({ label: t(HeaderText.profile), url: `${lfgHomeUrl}/secure/roleRouter?pageId=profileinfo`, externalLink: true });
            links.push({ label: t(HeaderText.settings), url: `${lfgHomeUrl}/secure/roleRouter?pageId=securitysettings`, externalLink: true });
        }
        else {
            links.push({ label: t(HeaderText.profileAndPermissions), url: `${lfgHomeUrl}/secure/roleRouter?pageId=profileinfo`, externalLink: true });
        }
    }
    else {
        links.push({ label: t(HeaderText.profile), url: '/profile/userInfo' });
        links.push({ label: t(HeaderText.settings), url: '/profile/password' });
    }
    if (isCommPrefEnabled) {
        links.push({ label: t(HeaderText.commPrefs), url: '/profile/alertsNotifications' });
    }
    if (!updatedEmployerEmailNotificationsEnabled && user.roles && (user.roles.includes('employer') || user.roles.indexOf('internal') > -1)) {
        links.push({ label: t(HeaderText.emailNotifications), url: '/admin/emailNotification' });
    }
    if (user.roles && (user.roles.indexOf('employeradmin') > -1 || user.roles.indexOf('internal') > -1) && user.customerInContext) {
        links.push({ label: t(HeaderText.userAccess), url: '/admin/permissions' });
    }
    links.push({ label: t(HeaderText.contactSupport), url: '/home/contacts' });
    return {
        heading: t({ english: 'Profile and support', spanish: 'Profile and support' }),
        categories: [{ links }],
    };
}
export function useFooterColumns(onNavigate) {
    const { navigationMenu, isCommPrefEnabled } = useCustomerConfig();
    const { isAuthenticated, user } = useAuthentication();
    const { t } = useTranslate();
    const { navigationCheck } = useRouteCheck();
    const applyCustomEmployeeText = useCustomEmployeeText();
    return buildFooterColumns({
        navigationMenu,
        isAuthenticated,
        user,
        isCommPrefEnabled,
        updatedEmployerEmailNotificationsEnabled: getEnvVariable('updatedEmployerEmailNotificationsEnabled'),
        lfgHomeUrl: getEnvVariable('lfgHomeUrl'),
        translate: (translatable) => applyCustomEmployeeText(t(translatable)),
        navigationCheck,
        onNavigate,
    });
}
